import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getCurrentContextName } from "supporting"
import { FlowType } from "../constants/flowType";
import { paymentMap } from "../payment/CheckOut/types";
import { PaymentCurrency } from "../constants/types";
import { changeCurrencyType, getProductsPrices, selectorOrder, setPaymentCurrencies, setTotalPrices, getAvailableTariffs } from "../slices/OrderSlice";
import { EntityPaymentTypes } from "../constants/payment";
import { CurrencyType } from "@brg/api";
import { CurrencyAccountType, getCurrencyType, searchCurrencyItem } from "../payment/CheckOut/helpers/utils";

const excludeMethods = [CurrencyType.Cashback]

export interface UseProductOrdering {
    onPaymentFail?: (params?: any) => void
    isTariffChoosing?: boolean
    setPaymentCurrency?: (currency: PaymentCurrency) => void
    competitionId?: string
}

export const useProductOrdering = (props: UseProductOrdering) => {
    const order = useSelector(selectorOrder)
    const dispatch = useDispatch();
    const { onPaymentFail, setPaymentCurrency, isTariffChoosing, competitionId } = props
    const context = getCurrentContextName();
    const [getPricesCounter, setGetPricesCounter] = useState(0);
    const [productsPrices, setProductsPrices] = useState(new Map<string, any>());

    useEffect(() => {
        const immediatePurchaseProductId = order?.immediatePurchaseProps?.productId;
        const isImmediateOk = immediatePurchaseProductId && order?.immediatePurchaseProps?.paymentEntityType;
        const flowType = isImmediateOk ? order?.immediatePurchaseProps?.flowType:  order?.flowType;

        if (!isImmediateOk) {
            return
        }

        if ([FlowType.BUY_BOOK].includes(flowType as FlowType)) {
            if (isTariffChoosing && order?.activeTariff?.creatingPrice) {
                const currency = {
                    currencyType: CurrencyType.RealMoney,
                    amount: order?.activeTariff?.creatingPrice ?? 0,
                    countryId: null 
                }
                setPaymentCurrency?.(currency);
                dispatch(setPaymentCurrencies([currency]))
                dispatch(changeCurrencyType(CurrencyType.RealMoney));                
            }
        }

        // Добавляем поддержку для карточек конкурса
        if ([FlowType.COPY_CARD].includes(flowType as FlowType)) {
            const currency = {
                currencyType: CurrencyType.RUB,
                amount: order?.immediatePurchaseProps?.price ?? 0,
                countryId: null 
            }
            setPaymentCurrency?.(currency);
            dispatch(setPaymentCurrencies([currency]))
            dispatch(changeCurrencyType(CurrencyType.RUB));
            
            // Загружаем тарифы для карточек
            dispatch(getAvailableTariffs({
                entityType: order?.immediatePurchaseProps?.paymentEntityType,
                productId: order?.immediatePurchaseProps?.productId
            }));
        }
    }, [
        dispatch,
        isTariffChoosing,
        order?.activeTariff?.creatingPrice,
        order?.immediatePurchaseProps?.productId,
        order?.immediatePurchaseProps?.paymentEntityType,
        order?.immediatePurchaseProps?.productInfo?.price,
        order?.immediatePurchaseProps?.flowType,
        order?.flowType,
        setPaymentCurrency
    ]);

    useEffect(() => {
        const immediatePurchaseProductId = order?.immediatePurchaseProps?.productId;
        const isImmediateOk = immediatePurchaseProductId && order?.immediatePurchaseProps?.paymentEntityType;
        const orderProductId = isImmediateOk ? immediatePurchaseProductId : order?.products?.at(0)?.id;
        const flowType = isImmediateOk ? order?.immediatePurchaseProps?.flowType:  order?.flowType;

        if (!isImmediateOk || isTariffChoosing || !flowType || [FlowType.BUY_BOOK].includes(flowType as FlowType)) {
            return
        }
        
        const entityPaymentType = order?.immediatePurchaseProps?.paymentEntityType

        const setInitialPrice = async (entityPaymentType: EntityPaymentTypes) => {
            if (order?.immediatePurchaseProps?.paymentEntityType === EntityPaymentTypes.print_card || !orderProductId) {                
                return
            }

            dispatch(getProductsPrices({
                id: orderProductId,
                type: entityPaymentType,
            }))
            //@ts-ignore
            .then(res => {
                if (!res?.payload?.prices) {
                    setGetPricesCounter(prev => prev + 1);

                    if (getPricesCounter >= 5) {
                        onPaymentFail && onPaymentFail({});
                        setGetPricesCounter(0);
                    }
                    return;
                }

                setProductsPrices( prev => prev.set(`${orderProductId}_${entityPaymentType}`, res.payload.prices));
                setGetPricesCounter(0);

                const paymentCurrencies = res.payload.prices
                
                let usedCurrency: PaymentCurrency | undefined
                if (competitionId) {
                    usedCurrency = searchCurrencyItem({
                        accountType: CurrencyAccountType.payment,
                        currencies: paymentCurrencies,
                        search: { 
                            countryId: "", 
                            currencyType: CurrencyType.RUB 
                        },
                    }) as PaymentCurrency | undefined
                }
                
                if (!usedCurrency) {
                    usedCurrency = searchCurrencyItem({
                        accountType: CurrencyAccountType.payment,
                        currencies: paymentCurrencies,
                        search: { 
                            countryId: "", 
                            currencyType: CurrencyType.RealMoney 
                        },
                    }) as PaymentCurrency | undefined
                }

                if (!usedCurrency) return

                // Формируем totalPrices из выбранного способа оплаты
                if ('parts' in usedCurrency) {
                    dispatch(setTotalPrices(usedCurrency.parts))
                }

                setPaymentCurrency?.(usedCurrency);
                dispatch(setPaymentCurrencies(paymentCurrencies))
            })
            .catch((err: any) => {
                onPaymentFail && onPaymentFail({ error: err })
            })
        }

        if (order?.products?.length && orderProductId) {
            if (!productsPrices.has(`${orderProductId}_${entityPaymentType}`))
            {
                setInitialPrice(entityPaymentType);
            } else {
                const paymentCurrencies = productsPrices.get(`${orderProductId}_${entityPaymentType}`);
                const usedCurrency = searchCurrencyItem({
                    accountType: CurrencyAccountType.payment,
                    currencies: paymentCurrencies,
                    search: { 
                        countryId: "", 
                        currencyType: CurrencyType.PlayMoney 
                    },
                }) as PaymentCurrency | undefined

                if (!usedCurrency) return
            }
        }
    }, [
        dispatch,
        isTariffChoosing,
        order?.immediatePurchaseProps?.productId,
        order?.immediatePurchaseProps?.paymentEntityType,
        order?.immediatePurchaseProps?.flowType,
        order?.flowType,
        order?.products,
        order?.products?.length,
        setPaymentCurrency,
        onPaymentFail,
        getPricesCounter,
        productsPrices,
        competitionId
    ]);

    const paymentCurrencies: PaymentCurrency[] = useMemo(() => {
        if (!order.paymentCurrencies?.length) {
            return order.paymentCurrencies
        }

        // Определяем тип заказа
        const immediatePurchaseProductId = order?.immediatePurchaseProps?.productId;
        const isImmediateOk = immediatePurchaseProductId && order?.immediatePurchaseProps?.paymentEntityType;
        const flowType = isImmediateOk ? order?.immediatePurchaseProps?.flowType : order?.flowType;

        return order.paymentCurrencies
            .filter((currencyItem: PaymentCurrency) => {
                const currencyType = getCurrencyType(currencyItem)
                // Для заказов типа COPY_CARD показываем только рублевые способы оплаты
                if (flowType === FlowType.COPY_CARD) {
                    return currencyType === CurrencyType.RUB
                }
                
                return true
            })
            .sort((a: PaymentCurrency, b: PaymentCurrency) => {
                //@ts-ignore
                return paymentMap[a.currencyType] - paymentMap[b.currencyType]
            })
    }, [context, order.paymentCurrencies])

    return {
        paymentCurrencies,
    }
}import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getCurrentContextName } from "supporting"
import { FlowType } from "../constants/flowType";
import { paymentMap } from "../payment/CheckOut/types";
import { PaymentCurrency } from "../constants/types";
import { changeCurrencyType, getProductsPrices, selectorOrder, setPaymentCurrencies, setTotalPrices, getAvailableTariffs } from "../slices/OrderSlice";
import { EntityPaymentTypes } from "../constants/payment";
import { CurrencyType } from "@brg/api";
import { CurrencyAccountType, getCurrencyType, searchCurrencyItem } from "../payment/CheckOut/helpers/utils";

const excludeMethods = [CurrencyType.Cashback]

export interface UseProductOrdering {
    onPaymentFail?: (params?: any) => void
    isTariffChoosing?: boolean
    setPaymentCurrency?: (currency: PaymentCurrency) => void
    competitionId?: string
}

export const useProductOrdering = (props: UseProductOrdering) => {
    const order = useSelector(selectorOrder)
    const dispatch = useDispatch();
    const { onPaymentFail, setPaymentCurrency, isTariffChoosing, competitionId } = props
    const context = getCurrentContextName();
    const [getPricesCounter, setGetPricesCounter] = useState(0);
    const [productsPrices, setProductsPrices] = useState(new Map<string, any>());

    useEffect(() => {
        const immediatePurchaseProductId = order?.immediatePurchaseProps?.productId;
        const isImmediateOk = immediatePurchaseProductId && order?.immediatePurchaseProps?.paymentEntityType;
        const flowType = isImmediateOk ? order?.immediatePurchaseProps?.flowType:  order?.flowType;

        if (!isImmediateOk) {
            return
        }

        if ([FlowType.BUY_BOOK].includes(flowType as FlowType)) {
            if (isTariffChoosing && order?.activeTariff?.creatingPrice) {
                const currency = {
                    currencyType: CurrencyType.RealMoney,
                    amount: order?.activeTariff?.creatingPrice ?? 0,
                    countryId: null 
                }
                setPaymentCurrency?.(currency);
                dispatch(setPaymentCurrencies([currency]))
                dispatch(changeCurrencyType(CurrencyType.RealMoney));                
            }
        }

        // Добавляем поддержку для карточек конкурса
        if ([FlowType.COPY_CARD].includes(flowType as FlowType)) {
            const currency = {
                currencyType: CurrencyType.RUB,
                amount: order?.immediatePurchaseProps?.price ?? 0,
                countryId: null 
            }
            setPaymentCurrency?.(currency);
            dispatch(setPaymentCurrencies([currency]))
            dispatch(changeCurrencyType(CurrencyType.RUB));
            
            // Загружаем тарифы для карточек
            dispatch(getAvailableTariffs({
                entityType: order?.immediatePurchaseProps?.paymentEntityType,
                productId: order?.immediatePurchaseProps?.productId
            }));
        }
    }, [
        dispatch,
        isTariffChoosing,
        order?.activeTariff?.creatingPrice,
        order?.immediatePurchaseProps?.productId,
        order?.immediatePurchaseProps?.paymentEntityType,
        order?.immediatePurchaseProps?.productInfo?.price,
        order?.immediatePurchaseProps?.flowType,
        order?.flowType,
        setPaymentCurrency
    ]);

    useEffect(() => {
        const immediatePurchaseProductId = order?.immediatePurchaseProps?.productId;
        const isImmediateOk = immediatePurchaseProductId && order?.immediatePurchaseProps?.paymentEntityType;
        const orderProductId = isImmediateOk ? immediatePurchaseProductId : order?.products?.at(0)?.id;
        const flowType = isImmediateOk ? order?.immediatePurchaseProps?.flowType:  order?.flowType;

        if (!isImmediateOk || isTariffChoosing || !flowType || [FlowType.BUY_BOOK].includes(flowType as FlowType)) {
            return
        }
        
        const entityPaymentType = order?.immediatePurchaseProps?.paymentEntityType

        const setInitialPrice = async (entityPaymentType: EntityPaymentTypes) => {
            if (order?.immediatePurchaseProps?.paymentEntityType === EntityPaymentTypes.print_card || !orderProductId) {                
                return
            }

            dispatch(getProductsPrices({
                id: orderProductId,
                type: entityPaymentType,
            }))
            //@ts-ignore
            .then(res => {
                if (!res?.payload?.prices) {
                    setGetPricesCounter(prev => prev + 1);

                    if (getPricesCounter >= 5) {
                        onPaymentFail && onPaymentFail({});
                        setGetPricesCounter(0);
                    }
                    return;
                }

                setProductsPrices( prev => prev.set(`${orderProductId}_${entityPaymentType}`, res.payload.prices));
                setGetPricesCounter(0);

                const paymentCurrencies = res.payload.prices
                
                let usedCurrency: PaymentCurrency | undefined
                if (competitionId) {
                    usedCurrency = searchCurrencyItem({
                        accountType: CurrencyAccountType.payment,
                        currencies: paymentCurrencies,
                        search: { 
                            countryId: "", 
                            currencyType: CurrencyType.RUB 
                        },
                    }) as PaymentCurrency | undefined
                }
                
                if (!usedCurrency) {
                    usedCurrency = searchCurrencyItem({
                        accountType: CurrencyAccountType.payment,
                        currencies: paymentCurrencies,
                        search: { 
                            countryId: "", 
                            currencyType: CurrencyType.RealMoney 
                        },
                    }) as PaymentCurrency | undefined
                }

                if (!usedCurrency) return

                // Формируем totalPrices из выбранного способа оплаты
                if ('parts' in usedCurrency) {
                    dispatch(setTotalPrices(usedCurrency.parts))
                }

                setPaymentCurrency?.(usedCurrency);
                dispatch(setPaymentCurrencies(paymentCurrencies))
            })
            .catch((err: any) => {
                onPaymentFail && onPaymentFail({ error: err })
            })
        }

        if (order?.products?.length && orderProductId) {
            if (!productsPrices.has(`${orderProductId}_${entityPaymentType}`))
            {
                setInitialPrice(entityPaymentType);
            } else {
                const paymentCurrencies = productsPrices.get(`${orderProductId}_${entityPaymentType}`);
                const usedCurrency = searchCurrencyItem({
                    accountType: CurrencyAccountType.payment,
                    currencies: paymentCurrencies,
                    search: { 
                        countryId: "", 
                        currencyType: CurrencyType.PlayMoney 
                    },
                }) as PaymentCurrency | undefined

                if (!usedCurrency) return
            }
        }
    }, [
        dispatch,
        isTariffChoosing,
        order?.immediatePurchaseProps?.productId,
        order?.immediatePurchaseProps?.paymentEntityType,
        order?.immediatePurchaseProps?.flowType,
        order?.flowType,
        order?.products,
        order?.products?.length,
        setPaymentCurrency,
        onPaymentFail,
        getPricesCounter,
        productsPrices,
        competitionId
    ]);

    const paymentCurrencies: PaymentCurrency[] = useMemo(() => {
        if (!order.paymentCurrencies?.length) {
            return order.paymentCurrencies
        }

        // Определяем тип заказа
        const immediatePurchaseProductId = order?.immediatePurchaseProps?.productId;
        const isImmediateOk = immediatePurchaseProductId && order?.immediatePurchaseProps?.paymentEntityType;
        const flowType = isImmediateOk ? order?.immediatePurchaseProps?.flowType : order?.flowType;

        return order.paymentCurrencies
            .filter((currencyItem: PaymentCurrency) => {
                const currencyType = getCurrencyType(currencyItem)
                // Для заказов типа COPY_CARD показываем только рублевые способы оплаты
                if (flowType === FlowType.COPY_CARD) {
                    return currencyType === CurrencyType.RUB
                }
                
                return true
            })
            .sort((a: PaymentCurrency, b: PaymentCurrency) => {
                //@ts-ignore
                return paymentMap[a.currencyType] - paymentMap[b.currencyType]
            })
    }, [context, order.paymentCurrencies])

    return {
        paymentCurrencies,
    }
}